import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useState } from "react";
import { X } from "lucide-react";

import { Button } from "@/design-system/primitives";
import { Icon } from "@/lib/Icon";
import { track } from "@/lib/analytics";
import { cn } from "@/lib/cn";
import { useMySubscriptions } from "@/lib/api/hooks";
import { subscriptionBlocksRepurchase, subscriptionPurchaseIdentity } from "../subscriptionOwnership";
import type {
  MarketplaceProduct,
  MarketplaceRecommendation,
} from "@/types/api";
import { cart, cartItemFromProduct, useCart } from "../stores/cart";
import { cartUi } from "../stores/cartUi";

function formatPrice(cents: number | null | undefined): string | null {
  if (cents == null) return null;
  const value = cents / 100;
  return value % 1 === 0 ? `$${value}` : `$${value.toFixed(2)}`;
}

export function LifecycleProductModal({
  open,
  onOpenChange,
  product,
  campaignKey = "",
  messageKey = null,
  recommendationId = null,
  recommendation,
  onAddToCart,
  onSwitchFormat,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: MarketplaceProduct | null;
  /** Lifecycle campaign metadata — omit for plain shop-card detail views. */
  campaignKey?: string;
  messageKey?: string | null;
  recommendationId?: string | null;
  /** Goal-based Shop recommendation. Present only for patients in the
   * personalized-recommendations treatment cohort. */
  recommendation?: MarketplaceRecommendation;
  onAddToCart?: () => void;
  /** Header label; shop-card opens use "PRODUCT DETAILS". */
  eyebrow?: string;
  /** Set when the patient already owns another FORMAT of this chemical —
   *  the CTA becomes "Switch to this format" and opens the switch flow
   *  instead of adding a duplicate subscription to the cart. */
  onSwitchFormat?: (product: MarketplaceProduct) => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Existing format switches keep their existing contract. They must never
  // display a paid dose option that the switch request cannot purchase.
  const purchaseOffers = onSwitchFormat ? undefined : product?.dose_offers;
  const { data: subscriptions } = useMySubscriptions();
  const ownedSubscription = subscriptions?.subscriptions.find(
    (subscription) =>
      subscriptionBlocksRepurchase(subscription) &&
      ((subscription.product_id && product?.canonical_product_id &&
        subscriptionPurchaseIdentity(subscription.product_id) ===
          subscriptionPurchaseIdentity(product.canonical_product_id)) ||
        product?.dose_offers?.some(
          (offer) => offer.dispensing_product_id === subscription.product_id,
        )),
  );
  const cartOfferId = useCart(
    (s) => s.items.find((item) => item.id === product?.id)?.dose_offer_id,
  );
  useEffect(() => {
    setSelectedId(
      cartOfferId ??
        purchaseOffers?.find((offer) => offer.is_default)?.id ??
        null,
    );
  }, [product?.id, cartOfferId, open, purchaseOffers]);
  const selectedOffer =
    purchaseOffers?.find((offer) => offer.id === selectedId) ??
    purchaseOffers?.find((offer) => offer.is_default);
  const owned = Boolean(product?.owned || ownedSubscription);
  const inCart = useCart((s) =>
    product ? s.items.some((item) => item.id === product.id) : false,
  );

  // V1 is a new-purchase flow only. A stale recommendation/deep link must not
  // expose an upgrade or allow a second subscription while ownership refreshes.
  if (!product || owned) return null;

  const price = formatPrice(
    selectedOffer?.plan_prices.monthly ?? product.price_cents,
  );

  const handleAdd = () => {
    if (onSwitchFormat) {
      track("marketplace_switch_format_opened", {
        product_id: product.id,
        slug: product.slug,
        source: "shop_card_detail",
        recommended: Boolean(recommendation),
        recommendation_rank: recommendation?.rank ?? null,
      });
      onOpenChange(false);
      onSwitchFormat(product);
      return;
    }
    if (!inCart || selectedOffer) {
      cart.add({
        ...cartItemFromProduct(product, selectedOffer),
        // Lifecycle attribution only when a campaign opened this modal —
        // plain shop-card opens stay unstamped.
        ...(campaignKey
          ? {
              lifecycle_campaign_key: campaignKey,
              lifecycle_message_key: messageKey ?? undefined,
              lifecycle_recommendation_id: recommendationId ?? undefined,
            }
          : {}),
      });
      track("marketplace_product_clicked", {
        source: campaignKey ? "lifecycle_product_upsell" : "shop_card_detail",
        campaign_key: campaignKey || null,
        message_key: messageKey,
        recommendation_id: recommendationId,
        product_id: product.id,
        slug: product.slug,
        price_cents: selectedOffer?.plan_prices.monthly ?? product.price_cents ?? 0,
        dose_offer_id: selectedOffer?.id,
        dose_offer_version: selectedOffer?.version,
        recommended: Boolean(recommendation),
        recommendation_rank: recommendation?.rank ?? null,
      });
      onAddToCart?.();
    }
    onOpenChange(false);
    cartUi.open();
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-dialog bg-black/20 backdrop-blur-[10px]" />
        <Dialog.Content
          className={cn(
            "fixed z-dialog left-1/2 top-1/2 w-[calc(100vw-30px)] md:max-w-[50rem]",
            "-translate-x-1/2 -translate-y-1/2 bg-page",
            "h-[calc(100vh-30px)] lg:h-[unset] lg:max-h-[calc(100vh-60px)] flex flex-col overflow-hidden shadow-modal focus-visible:outline-none",
          )}
        >
          {/* Close — absolute to the non-scrolling modal, so it stays pinned
              to the corner while only the body below scrolls. */}
          <Dialog.Close asChild>
            <button
              type="button"
              aria-label="Close recommendation"
              className="absolute z-20 bg-[#f5f5f5] top-5 right-5 w-45 h-45 inline-flex items-center justify-center text-ink-secondary hover:text-ink"
            >
              <X size={18} strokeWidth={1.5} />
            </button>
          </Dialog.Close>

          {/* Scrolling body — the only scroll region. overflow-x-hidden keeps
              the modal from ever scrolling sideways. */}
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
            <div className="grid grid-cols-1">
              <div className="relative min-h-[28rem] bg-sunken bg-[url('/icons/system_scene_background.jpg')] bg-cover bg-center overflow-hidden">
                {/* Zero-background product render composited over the shared
                    scene backdrop (same pattern as the stack + recommendation
                    cards). Falls back to the standard hero photo when no
                    transparent render exists yet. */}
                {product.scene_image_url ? (
                  <img
                    src={product.scene_image_url}
                    alt={product.title}
                    className="absolute inset-0 w-full h-full object-contain p-30"
                  />
                ) : product.hero_image_url ? (
                  <img
                    src={product.hero_image_url}
                    alt={product.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center type-label-sm text-ink-muted">
                    PRODUCT IMAGE
                  </div>
                )}
                {price && (
                  <div className="absolute left-5 top-5 bg-black/60 text-white backdrop-blur-md px-12 py-5 flex items-end gap-2">
                    <span>{price}</span>
                    <span className="text-10 pb-3">
                      {product.purchase_mode === "one_time_course"
                        ? "one-time course"
                        : "/ mo."}
                    </span>
                  </div>
                )}

                <div className="absolute bottom-0 left-0 right-0 p-5">
                  {recommendation && (
                    <div className="flex flex-col gap-5 bg-black/50 text-white px-12 py-10 backdrop-blur-[20px]">
                      <span className="shrink-0 type-label-xs uppercase opacity-70">
                        Recommended for you
                      </span>
                      <span className="type-body-sm leading-snug">
                        {recommendation.reason}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col">
                {/* {recommendation && (
                  <div className="flex flex-col gap-5 bg-[#707889] text-white px-12 py-10 sm:flex-row sm:items-center sm:gap-10">
                    <span className="shrink-0 type-label-xs uppercase opacity-70">
                      Recommended for you
                    </span>
                    <span className="type-body-sm italic leading-snug">
                      {recommendation.reason}
                    </span>
                  </div>
                )} */}
                <div className="flex flex-col p-15 gap-5">
                  <div className="flex flex-col gap-5">
                    <Dialog.Title className="type-title-md">
                      {(selectedOffer?.title ?? product.title).toUpperCase()}
                    </Dialog.Title>
                    {(selectedOffer ||
                      product.treatment_support ||
                      product.subtitle) && (
                      <Dialog.Description className="type-body-md">
                        {selectedOffer?.contents ??
                          product.treatment_support ??
                          product.subtitle}
                      </Dialog.Description>
                    )}
                  </div>
                  {Boolean(purchaseOffers?.length) && (
                    <fieldset className="py-15">
                      <legend className="font-mono text-12 uppercase">
                        Monthly supply · every 28 days
                      </legend>
                      <div className="flex border border-line-default mt-10">
                        {purchaseOffers?.map((offer) => (
                          <label
                            key={offer.id}
                            className="flex-1 cursor-pointer"
                          >
                            <input
                              className="sr-only peer"
                              type="radio"
                              name={`dose-${product.id}`}
                              value={offer.id}
                              checked={selectedOffer?.id === offer.id}
                              onChange={() => setSelectedId(offer.id)}
                            />
                            <span className="flex min-h-44 items-center justify-center gap-5 px-10 border border-transparent peer-checked:bg-sunken peer-checked:border-ink peer-focus-visible:ring-2 peer-focus-visible:ring-accent text-14">
                              {offer.label}
                            </span>
                          </label>
                        ))}
                      </div>
                      <p className="text-12 text-ink-secondary mt-10">
                        This is the total supply, not the amount per injection.
                        Your clinician determines your prescription directions.
                      </p>
                    </fieldset>
                  )}
                  <div className="flex flex-col">
                    {product.description && (
                      // Arrow-indent block, mirroring the PageHeader description
                      // pattern: relative wrapper with the first line indented
                      // and the arrow hanging at its left edge.
                      <div className="relative indent-15">
                        <div
                          aria-hidden
                          className="absolute top-4 left-0 text-[#A9A9A9]"
                        >
                          <Icon
                            name="Arrow"
                            viewBox="0 0 10 12"
                            className="w-[.8rem]"
                          />
                        </div>
                        <p className="type-body-sm text-ink-secondary leading-130">
                          {product.description}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-1 flex flex-col border-t border-line-subtle pt-5">
                  {/* {detailRows.length > 0 && (
                    <dl className="grid grid-cols-1 sm:grid-cols-3">
                      {detailRows.map(([label, value]) => (
                        <div
                          key={label}
                          className="py-12 sm:px-12 sm:first:pl-0 sm:border-l sm:first:border-l-0 border-line-subtle"
                        >
                          <dt className="type-label-xs text-ink-muted mb-6">
                            {label}
                          </dt>
                          <dd className="type-body-sm text-ink-secondary m-0">
                            {value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  )} */}

                  {product.value_props?.length ? (
                    <ul className="grid grid-cols-1 pb-10">
                      {product.value_props.slice(0, 4).map((value) => (
                        <li
                          key={value}
                          className="flex items-start gap-8 text-12 text-ink px-12 py-5"
                        >
                          <span className="shrink-0 w-12 h-12 inline-flex items-center justify-center rounded-[999rem] overflow-hidden mt-3">
                            <img
                              src="/icons/icon-check.png"
                              alt=""
                              aria-hidden="true"
                              className="w-full h-full object-cover invert"
                            />
                          </span>
                          <span>{value}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {/* CTA footer — sits outside the scroll region, so the add-to-cart
              action and its note stay visible at all times. */}
          <div className="shrink-0 border-t border-line-subtle bg-page p-15 flex flex-col gap-10">
            <Button
              type="button"
              variant="filled"
              className="w-full"
              onClick={handleAdd}
            >
              <span>
                {onSwitchFormat
                    ? "Switch to this format"
                    : inCart
                      ? "Review cart"
                      : "Add to cart"}
              </span>
              <span
                aria-hidden
                className="inline-flex items-center justify-center w-8"
              >
                <Icon name="Arrow" viewBox="0 0 10 12" />
              </span>
            </Button>
            <p className="text-9 text-ink-muted">
              Your clinician reviews anything that needs clinical approval before it ships.
            </p>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
